import { Button, Colors, Switch, Tooltip, type VirtualTableColumn } from "@snapchat/snap-design-system";
import { InformationIcon, TrashIcon } from "@snapchat/snap-design-system-icons";

import { CopyToClipboardButton, LensIcon } from "../../camkit/common/components";
import { TagWithTooltip } from "../../camkit/common/components/TagWithTooltip";
import { DecreaseText } from "../../camkit/common/components/text";
import { Flexbox } from "../../camkit/core/components";
import { type HostedLens, HostedLensStatus } from "../../gql-generated/types";
import { IconSection, LensStatusIndicator } from "../../lens/components/shared";
import { type HostedLensListItem } from "../hooks/useHostedLens";

import { Anchor, indicatorCSS, tooltipStatusWrapper } from "./HostedLensSection.styles";
import { HostedLensStatusCell } from "./HostedLensStatusCell";
import { enableWrapperCss } from "./HostedLensTableColumns.styles";
import {
    HostedLensDeletedTooltipText,
    SectionLensStatusLabel,
    SectionLensURLLabel,
    SectionStatusTooltipContent,
    SectionStatusTooltipItemOne,
    SectionStatusTooltipItemTwo,
} from "./messages.generated";

interface CellRenderer {
    row: {
        index: number;
    };
    data: Readonly<HostedLens[]>;
}

interface GetColumnsProps {
    onDelete: (id: string) => void;
    handleToggle: (isEnabled: boolean, id: string) => void;
}

const getRenderer = (fn: (hostedLens: HostedLens) => React.JSX.Element) => {
    return ({ row, data }: CellRenderer) => fn(data[row.index] ?? ({} as HostedLens));
};

export const getColumns = ({ onDelete, handleToggle }: GetColumnsProps): VirtualTableColumn<HostedLensListItem>[] => {
    return [
        {
            id: "iconUrl",
            width: 72,
            Cell: getRenderer((lens) => (
                <IconSection data-testid="lens-row-icon">
                    <LensIcon
                        src={lens.hostedLensMetadata?.iconUrl}
                        alt={`${lens.hostedLensMetadata?.name} Lens Icon`}
                    />
                </IconSection>
            )),
        },
        {
            id: "name",
            width: 250,
            Header: "Name",
            Cell: getRenderer((lens) => {
                if (lens.hostedLensMetadata === null) {
                    return (
                        <>
                            <TagWithTooltip
                                tag={{
                                    status: "error",
                                    text: "Lens was deleted",
                                }}
                                tooltipText={<HostedLensDeletedTooltipText />}
                            />
                        </>
                    );
                }
                return <DecreaseText>{lens.hostedLensMetadata?.name}</DecreaseText>;
            }),
        },
        {
            id: "link",
            Header: <SectionLensURLLabel />,
            width: 315,
            Cell: getRenderer((lens) => (
                <>
                    <Anchor
                        href={lens.link}
                        title={lens.link}
                        referrerPolicy="no-referrer"
                        data-testid="hosted-lens-link"
                        target="_blank"
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        {lens.link}
                    </Anchor>
                    <CopyToClipboardButton
                        tooltip={`Copy ${lens.link}`}
                        copyValue={lens.link}
                        data-testid="hosted-lens-copy-field"
                        notification={{ title: `${lens.link} copied to clipboard` }}
                        testId={lens.link.split(" ").join(".").toLocaleLowerCase()}
                    />
                </>
            )),
        },
        {
            id: "status",
            width: 200,
            Header: (
                <>
                    <SectionLensStatusLabel />
                    <Tooltip
                        content={
                            <div className={tooltipStatusWrapper}>
                                <SectionStatusTooltipContent />
                                <Flexbox>
                                    <LensStatusIndicator className={indicatorCSS} background={Colors.Green.V150} />
                                    <SectionStatusTooltipItemOne />
                                </Flexbox>
                                <Flexbox>
                                    <LensStatusIndicator className={indicatorCSS} background={Colors.Orange.V200} />
                                    <SectionStatusTooltipItemTwo />
                                </Flexbox>
                            </div>
                        }
                    >
                        <InformationIcon fill={Colors.Gray.V250} size={16} />
                    </Tooltip>
                </>
            ),
            Cell: getRenderer((lens) => (
                <>
                    <HostedLensStatusCell data-testid="hosted-lens-status" status={lens.status} />{" "}
                </>
            )),
        },
        {
            id: "enable",
            Cell: getRenderer((lens) => (
                <div className={enableWrapperCss}>
                    <Switch
                        isChecked={lens.status === HostedLensStatus.HostedLensStatusPublished}
                        onClick={(isEnabled: boolean, event) => {
                            event.stopPropagation();
                            handleToggle(isEnabled, lens.id);
                        }}
                    />
                    <Button
                        shape="circle"
                        onClick={(event) => {
                            event.stopPropagation();
                            onDelete(lens.id);
                        }}
                    >
                        <TrashIcon />
                    </Button>
                </div>
            )),
        },
    ];
};
